import React, { useEffect, useState, useCallback } from "react";
import GraphSangatiRight from "../components/graphs/GraphSangatiRight";
import GraphSangatiLeft from "../components/graphs/GraphSangatiLeft";
import Quadrants from "../components/Sangati/Quadrants";
import Settings from "../components/Settings";
import MatcheeInput from "../components/MatcheeInput";
import { useLocation, useNavigate } from "react-router-dom";
import { useMode } from "../context/useMode";
import PageLoader from "./PageLoader";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import ActivityNotificationButton from "../components/NotificationButton";

import useRadarResource from "../resources/useRadarResource";
import useUsersResource from "../resources/useUsersResource";

const Sangati = () => {
  const location = useLocation();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { setMode } = useMode();
  const [graphData, setGraphData] = useState(null);
  const [matcheeGraphData, setMatcheeGraphData] = useState(null);
  const [showSettings, setShowSettings] = useState(false);
  const [quadrants, setQuadrants] = useState({
    lightmine: false,
    darkmine: false,
    lightother: false,
    darkother: false,
  });
  const [leftToggle, setLeftToggle] = useState(false);
  const [rightToggle, setRightToggle] = useState(false);
  const [sent, setSent] = useState(false);
  const [username, setUsername] = useState("");
  const [showCompatibility, setShowCompatibility] = useState(false);
  const [showReadyCircle, setShowReadyCircle] = useState(false);
  const [handle, setHandle] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [userData, setUserData] = useState(null);
  const [compatibilityScore, setCompatibilityScore] = useState(null);
  const [matchNotAllowed, setMatchNotAllowed] = useState(false);
  const [modeMatch, setModeMatch] = useState(null);

  /* Used to get the radar data for a user */
  const { getRadar } = useRadarResource();
  /* Used to get the user data */
  const { getUser, findByHandle } = useUsersResource();

  /**
   * Initialize the toggle buttons
   * MatchMode:
   *  null|undefined = Show only user toggle buttons based on user data
   *    - light_mode_active = Enable or disable light mode radar and pie
   *    - dark_mode_active = Enable or disable dark mode radar and pie
   *  light_mode = Show both users light mode toggles etc
   *  dark_mode = Show both users dark mode toggles etc
   *  both_mode = Show both users light and dark mode toggles etc
   */
  useEffect(() => {
    if (userData && !userData.first_time_status && modeMatch === null) {
      setLeftToggle(userData.light_mode_active);
      setRightToggle(userData.dark_mode_active);
    } else if (modeMatch) {
      setLeftToggle(modeMatch === "light_mode" || modeMatch === "both_mode");
      setRightToggle(modeMatch === "dark_mode" || modeMatch === "both_mode");
    }
  }, [userData, modeMatch]);

  /**
   * Collects the radar data for the user and updates the quadrants.
   */
  useEffect(() => {
    if (!handle) return;
    const GetUserRadarData = async () => {
      try {
        setIsLoading(true);
        const res = await getRadar(handle.user.handle);
        setGraphData(res);
        if (!res.light || Object.keys(res.light).length === 0) {
          setQuadrants(prev => ({ ...prev, lightmine: false }));
        } else {
          setQuadrants(prev => ({ ...prev, lightmine: true }));
        }
        if (!res.dark || Object.keys(res.dark).length === 0) {
          setQuadrants(prev => ({ ...prev, darkmine: false }));
        } else {
          setQuadrants(prev => ({ ...prev, darkmine: true }));
        }
      } catch {
        // Optionally handle error
      } finally {
        setIsLoading(false);
      }
    };
    GetUserRadarData();
  }, [getRadar, handle]);

  const handleLightModeClick = () => {
    setMode("light");
    navigate("/questionnaire");
  };

  const handleDarkModeClick = () => {
    setMode("dark");
    navigate("/dark-warning");
  };

  const clearmatchee = () => {
    setQuadrants(prev => ({
      ...prev,
      lightother: false,
      darkother: false,
    }));
    setShowReadyCircle(false);
    setShowCompatibility(false);
    setSent(false);
    setMatchNotAllowed(false);
    setLeftToggle(userData.light_mode_active);
    setRightToggle(userData.dark_mode_active);
    setUsername("");
    setMatcheeGraphData(null);
    setCompatibilityScore(null);
    setModeMatch(null);
    localStorage.removeItem("sangatiState");
    navigate(location.pathname, { replace: true, state: null });
  };

  // useEffects for state persistence
  useEffect(() => {
    const savedStateRow = localStorage.getItem("sangatiState");
    const savedState = savedStateRow ? JSON.parse(savedStateRow) : null;
    if (savedState) {
      setUsername(savedState.username);
      setMatcheeGraphData(savedState.matcheeGraphData);
      setShowCompatibility(savedState.showCompatibility);
      setShowReadyCircle(savedState.showReadyCircle);
      setSent(savedState.sent);
      setQuadrants(savedState.quadrants);
      setCompatibilityScore(savedState.compatibilityScore);
      setModeMatch(savedState.modeMatch);
    }
  }, []);

  useEffect(() => {
    const isEmpty =
      !username &&
      !matcheeGraphData &&
      !showCompatibility &&
      !showReadyCircle &&
      !sent &&
      (!quadrants ||
        (!quadrants.lightmine &&
          !quadrants.darkmine &&
          !quadrants.lightother &&
          !quadrants.darkother)) &&
      !compatibilityScore &&
      !modeMatch;
    if (isEmpty) {
      localStorage.removeItem("sangatiState");
    } else {
      const stateToSave = {
        username,
        matcheeGraphData,
        showCompatibility,
        showReadyCircle,
        sent,
        quadrants,
        compatibilityScore,
        modeMatch,
      };
      localStorage.setItem("sangatiState", JSON.stringify(stateToSave));
    }
  }, [
    username,
    matcheeGraphData,
    showCompatibility,
    showReadyCircle,
    sent,
    quadrants,
    compatibilityScore,
    modeMatch,
  ]);

  /**
   * When the user selected a matchee we update the display depending on the state.
   * Handles two different flows:
   * 1. userData flow: From SearchPage selection (determines modeMatch from user's enabled modes)
   * 2. compatibilityScore: Optional compatibility data
   */
  useEffect(() => {
    if (location.state?.matchHandle) {
      setUsername(location.state.matchHandle);

      // Handle compatibility score if provided
      if (location.state.compatibilityScore) {
        setCompatibilityScore(location.state.compatibilityScore);
      }

      // Handle userData flow (from SearchPage)
      if (location.state.userData) {
        const { light_mode_active, dark_mode_active } = location.state.userData;

        // Determine modeMatch based on enabled modes
        const modeMatch =
          light_mode_active && dark_mode_active
            ? "both_mode"
            : light_mode_active
              ? "light_mode"
              : dark_mode_active
                ? "dark_mode"
                : null;

        setModeMatch(modeMatch);
        setQuadrants(prev => ({
          ...prev,
          lightother: light_mode_active,
          darkother: dark_mode_active,
        }));
      }
    }
  }, [location.state]);

  /**
   * When the user clicks on an entry in the activity page it redirects here with state data.
   * This data indicate what the match was like.
   */
  useEffect(() => {
    const getMatcheeRadar = async () => {
      const res = await getRadar(location.state.activity.match_user?.handle);
      setMatcheeGraphData(res);
    };
    if (location.state?.activity) {
      const activity = location.state.activity;
      setUsername(activity.match_user?.handle || activity.user?.handle);
      getMatcheeRadar();

      setQuadrants(prev => ({
        ...prev,
        lightother:
          activity.mode_match === "light_mode" ||
          activity.mode_match === "both_mode",
        darkother:
          activity.mode_match === "dark_mode" ||
          activity.mode_match === "both_mode",
      }));
      setCompatibilityScore({
        avg:
          activity.mode_match === "both_mode"
            ? Number(activity.compatibility_score)
            : undefined,
        light:
          activity.mode_match === "light_mode"
            ? Number(activity.compatibility_score)
            : undefined,
        dark:
          activity.mode_match === "dark_mode"
            ? Number(activity.compatibility_score)
            : undefined,
      });
      setShowReadyCircle(activity.match_type !== "echo_match");
      setShowCompatibility(activity.match_type !== "echo_match");
      setModeMatch(activity.mode_match);
    }
  }, [getRadar, location.state]);

  useEffect(() => {
    const qrHandleFromState = location.state?.matcheeqrHandle;
    if (qrHandleFromState) {
      navigate(location.pathname, { replace: true, state: null });
    }
  }, [location.pathname, location.state?.matcheeqrHandle, navigate]);

  useEffect(() => {
    const rawHandle = localStorage.getItem("handle");
    const handle = rawHandle ? JSON.parse(rawHandle) : null;
    setHandle(handle);
  }, []);

  const rawHandle = localStorage.getItem("handle");
  const userhandle = rawHandle ? JSON.parse(rawHandle) : null;

  const getUserData = useCallback(async () => {
    try {
      const user = await getUser(userhandle.user.id);
      setUserData(user);
    } catch (error) {
      console.log(error, "Error in Get User API");
    }
  }, [getUser, userhandle.user.id]);

  useEffect(() => {
    getUserData();
  }, [getUserData]);

  // When matchee name changes and there is no match/activity, fetch their mode info and update toggles/quadrants
  useEffect(() => {
    if (showCompatibility || compatibilityScore) return;
    // Only run if username is set, there is no activity, and getMatch returns 404 (handled by not setting matcheeGraphData)
    // Also don't run if we have userData from location.state (our new logic handles this)
    if (!username || location.state?.activity || location.state?.userData)
      return;
    const fetchOtherUserModes = async () => {
      try {
        const matchee = await findByHandle(username);
        // Only allow modes that both users have enabled
        const allowLight =
          matchee.light_mode_active && userData.light_mode_active;
        const allowDark = matchee.dark_mode_active && userData.dark_mode_active;
        setQuadrants(prev => ({
          ...prev,
          lightother: allowLight,
          darkother: allowDark,
        }));
        if (!allowLight && !allowDark) {
          setMatchNotAllowed(true);
          setCompatibilityScore(null);
          setModeMatch(null);
        } else {
          setMatchNotAllowed(false);
        }
      } catch (err) {
        setQuadrants(prev => ({
          ...prev,
          lightother: false,
          darkother: false,
        }));
        setMatchNotAllowed(false);
        setCompatibilityScore(null);
        setModeMatch(null);
      }
    };
    fetchOtherUserModes();
  }, [
    username,
    location.state?.activity,
    location.state?.userData,
    findByHandle,
    userData,
    t,
    showCompatibility,
    compatibilityScore,
  ]);

  // Add this effect to re-run matchee compatibility logic after mode toggle
  useEffect(() => {
    if (showCompatibility || compatibilityScore) return;
    // Don't run if we have userData from location.state (our new logic handles this)
    if (
      username &&
      userData &&
      !location.state?.activity &&
      !location.state?.userData
    ) {
      const fetchOtherUserModes = async () => {
        try {
          const matchee = await findByHandle(username);
          const allowLight =
            matchee.light_mode_active && userData.light_mode_active;
          const allowDark =
            matchee.dark_mode_active && userData.dark_mode_active;
          setQuadrants((prev) => ({
            ...prev,
            lightother: allowLight,
            darkother: allowDark,
          }));
          if (!allowLight && !allowDark) {
            setMatchNotAllowed(true);
            setCompatibilityScore(null);
            setModeMatch(null);
          } else {
            setMatchNotAllowed(false);
          }
        } catch (err) {
          // ignore errors
        }
      };
      fetchOtherUserModes();
    }
  }, [
    userData,
    username,
    findByHandle,
    location.state?.activity,
    location.state?.userData,
    showCompatibility,
    compatibilityScore,
  ]);

  return (
    <>
      {isLoading && <PageLoader />}
      <div className="relative w-full max-w-[786px] mx-auto h-dvh overflow-hidden flex flex-col bg-bg-grey">
        <div className="flex gap-2 w-full px-2 pt-3 relative">
          <div className="w-1/2 flex-1">
            {quadrants.lightmine ? (
              <div
                className={`${leftToggle ? "bg-white85 border-div" : ""
                  } rounded-[10px] flex flex-col pb-8 relative`}
              >
                <div>
                  <GraphSangatiLeft
                    islightdisabled={!leftToggle}
                    showCompatibility={showCompatibility}
                    lightData={graphData?.light}
                    {...(username &&
                      quadrants.lightother && {
                      matcheelightdata: matcheeGraphData?.light,
                    })}
                  />
                </div>
              </div>
            ) : (
              <div className="flex justify-center items-center w-full h-full">
                <div
                  className="p-2 bg-white85 rounded-[10px] text-center px-4 text-black"
                  onClick={handleLightModeClick}
                >
                  {t("complete")}
                </div>
              </div>
            )}
          </div>
          <div className="w-1/2 flex-1">
            {quadrants.darkmine ? (
              <div
                className={`${rightToggle ? "bg-black85 border-div" : ""
                  } rounded-[10px] flex flex-col pb-8 relative`}
              >
                <div>
                  <GraphSangatiRight
                    isdarkdisabled={!rightToggle}
                    showCompatibility={showCompatibility}
                    darkData={graphData?.dark}
                    {...(username &&
                      quadrants.darkother && {
                      matcheedarkdata: matcheeGraphData?.dark,
                    })}
                  />
                </div>
              </div>
            ) : (
              <div className="flex justify-center items-center w-full h-full">
                <div
                  className="p-2 bg-black85 rounded-[10px] text-center px-4 text-white"
                  onClick={handleDarkModeClick}
                >
                  {t("complete")}
                </div>
              </div>
            )}
          </div>
          <div
            className="cursor-pointer absolute left-1/2 -bottom-9 -translate-x-1/2 -translate-y-1/2 bg-white border-[2px] w-[calc(100%-40px)] justify-between border-[#00000020] rounded-[10px] flex items-center px-4 py-2 gap-3 shadow-lg z-20"
            onClick={() => setShowSettings(true)}
          >
            <div className="flex gap-2 items-center">
              <div className="rounded-full border border-black bg-[#BEEDF3] w-5 h-5"></div>
              <p className="text-black text-14 font-serif break-words gap-1 flex">
                <span className="text-[#93938D]">{t("sangatiLive")}/</span>
                {userhandle?.user?.handle}
              </p>
            </div>

            <div className="flex gap-2 items-center absolute right-0 top-1 pr-2 bg-white">
              <img
                src="/images/shared.png"
                alt="copy"
                className="w-5 h-5"
                onClick={event => {
                  event.stopPropagation();
                  const username = userhandle?.user?.handle;
                  if (username) {
                    navigator.clipboard
                      .writeText(username)
                      .then(() => {
                        toast.success("Username copied!", {
                          position: "bottom-center",
                          autoClose: 1500,
                          className: "custom-toastify",
                          icon: false,
                        });
                      })
                      .catch(err => {
                        toast.error("Failed to copy.", {
                          position: "bottom-center",
                          autoClose: 1500,
                          className: "custom-toastify",
                          icon: false,
                        });
                        console.error("Copy error:", err);
                      });
                  }
                }}
              />
              <img
                src="/icons/Sangati/user-settings.svg"
                alt="settings"
                className="w-6 h-6"
                onClick={() => setShowSettings(true)}
              />
            </div>
          </div>
        </div>

        {/* slide up part */}
        <Settings
          setShowSettings={setShowSettings}
          showSettings={showSettings}
        />

        <div className="flex flex-col mt-10">
          <div>
            <Quadrants
              lightmine={quadrants.lightmine}
              darkmine={quadrants.darkmine}
              lightother={quadrants.lightother}
              darkother={quadrants.darkother}
              islightdisabled={!leftToggle}
              isdarkdisabled={!rightToggle}
              showCompatibility={showCompatibility}
              setShowCompatibility={setShowCompatibility}
              leftToggle={leftToggle}
              rightToggle={rightToggle}
              setLeftToggle={setLeftToggle}
              setRightToggle={setRightToggle}
              graphData={graphData}
              matchee_username={username}
              setMatcheeGraphData={setMatcheeGraphData}
              showReadyCircle={showReadyCircle}
              setShowReadyCircle={setShowReadyCircle}
              bankbalance={userData?.credit_balance}
              sent={sent}
              setSent={setSent}
              getUserData={getUserData}
              firsttimestatus={userData?.first_time_status}
              compatibilityScore={compatibilityScore}
              modeMatch={modeMatch}
              setModeMatch={setModeMatch}
              setCompatibilityScore={setCompatibilityScore}
              matchNotAllowed={matchNotAllowed}
              setMatchNotAllowed={setMatchNotAllowed}
            />
          </div>

          {/* Matchee Mode boxes, light and dark  */}
          <div className="relative flex gap-x-4 items-center justify-center mt-6">
            {username && (
              <>
                {/* Matchee Light Mode Box */}
                {quadrants.lightother ? (
                  <div className="w-[180px] h-[60px] border-div other-handle-left bg-white85 rounded-[20px] rounded-tr-none shadow-md" />
                ) : (
                  <div className="w-[180px] h-[60px] invisible" />
                )}
                {/* Matchee Dark Mode Box */}
                {quadrants.darkother ? (
                  <div className="w-[180px] h-[60px] border-div other-handle-right bg-black85 rounded-[20px] rounded-tl-none shadow-md" />
                ) : (
                  <div className="w-[180px] h-[60px] invisible" />
                )}
              </>
            )}

            {/* Matchee Button or display, shows on top of matchee mode boxes */}
            {!userData?.first_time_status && (
              <MatcheeInput
                username={username}
                onClearUsername={clearmatchee}
              />
            )}
          </div>
        </div>
        {/* Small activity indicator and button at bottom of page */}
        {!userData?.first_time_status && (
          <ActivityNotificationButton clearmatchee={clearmatchee} />
        )}
      </div>
    </>
  );
};

export default Sangati;
